import {QuizSession} from '@cp/model';
import {Close, DeleteSweep, PlayArrow} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {env} from '../../config/env';
import useEndQuizSession from '../../hooks/quizSession/useEndQuizSession';
import useListUserActiveSession from '../../hooks/quizSession/useListUserActiveSession';

export default function ExerciseSessionActive() {
  const navigate = useNavigate();
  const {activeSessions, loading} = useListUserActiveSession();
  const {endQuizSession} = useEndQuizSession();
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [closingSessionId, setClosingSessionId] = useState<string | null>(null);
  const [closingAll, setClosingAll] = useState(false);
  const [alert, setAlert] = useState<{type: 'error' | 'success'; message: string} | null>(null);

  // Auto-select the session if there's only one
  useEffect(() => {
    if (activeSessions.length === 1) {
      setSelectedSessionId(activeSessions[0].id);
    }
  }, [activeSessions]);

  // Auto-dismiss success messages after 3 seconds
  useEffect(() => {
    if (alert?.type === 'success') {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [alert]);

  const handleSessionChange = (event: SelectChangeEvent) => {
    setSelectedSessionId(event.target.value);
  };

  const handleResumeSession = () => {
    if (selectedSessionId) {
      navigate(`/session/${selectedSessionId}`);
    }
  };

  const handleCloseSession = async (sessionId: string) => {
    try {
      setClosingSessionId(sessionId);
      setAlert(null);
      await endQuizSession(sessionId);
      if (selectedSessionId === sessionId) {
        setSelectedSessionId('');
      }
      setAlert({type: 'success', message: 'Session closed successfully!'});
    } catch (err) {
      setAlert({type: 'error', message: 'Failed to close session. Please try again.'});
      console.error('Error closing session:', err);
    } finally {
      setClosingSessionId(null);
    }
  };

  const handleCloseAllSessions = async () => {
    try {
      setClosingAll(true);
      setAlert(null);

      const sessionCount = activeSessions.length;

      await Promise.all(activeSessions.map((session) => endQuizSession(session.id)));

      setSelectedSessionId('');
      setAlert({
        type: 'success',
        message: `${sessionCount} session${sessionCount === 1 ? '' : 's'} closed successfully!`,
      });
    } catch (err) {
      setAlert({type: 'error', message: 'Failed to close some sessions. Please try again.'});
      console.error('Error closing all sessions:', err);
    } finally {
      setClosingAll(false);
    }
  };

  const formatSessionInfo = (session: QuizSession) => {
    const answeredCount = session.questions.filter((q) => q.selectedAnswer).length;
    const totalQuestions = session.questions.length;
    const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

    return `${session.moduleName} - ${progress}% complete (${answeredCount}/${totalQuestions})`;
  };

  const formatSessionDate = (session: QuizSession) => dayjs(session.startedAt).format('MMM D, YYYY [at] h:mm A');

  // Don't render anything if loading or no active sessions
  if (loading || activeSessions.length === 0) {
    return null;
  }

  return (
    <Paper sx={{p: 3}}>
      <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
        Active Sessions ({activeSessions.length})
      </Typography>

      {alert && (
        <Alert severity={alert.type} sx={{mb: 3}}>
          {alert.message}
        </Alert>
      )}

      <Box sx={{mb: 3}}>
        <FormControl fullWidth>
          <InputLabel id="active-session-select-label">Select Session to Resume</InputLabel>
          <Select
            labelId="active-session-select-label"
            value={selectedSessionId}
            label="Select Session to Resume"
            onChange={handleSessionChange}
          >
            {activeSessions.map((session) => (
              <MenuItem key={session.id} value={session.id}>
                <Box sx={{width: '100%'}}>
                  <Typography variant="body1" sx={{fontWeight: 500}}>
                    {formatSessionInfo(session)}
                  </Typography>
                  <Typography variant="body2" sx={{color: 'text.secondary', mt: 0.5}}>
                    Started {formatSessionDate(session)}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{mt: 1}}>
                    <Chip label={session.timedMode.replace('_', ' ')} size="small" variant="outlined" color="primary" />
                    {session.difficulty ? (
                      <Chip
                        label={`Difficulty ${session.difficulty}`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    ) : (
                      <Chip label={env.label.levelAuto} size="small" variant="outlined" color="secondary" />
                    )}
                  </Stack>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Stack spacing={2}>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={handleResumeSession}
          disabled={!selectedSessionId}
          fullWidth
          size="large"
        >
          Resume Selected Session
        </Button>

        {selectedSessionId && (
          <Button
            variant="outlined"
            startIcon={closingSessionId === selectedSessionId ? <CircularProgress size={20} /> : <Close />}
            onClick={() => handleCloseSession(selectedSessionId)}
            disabled={closingSessionId === selectedSessionId || closingAll}
            fullWidth
            color="error"
          >
            Close Selected Session
          </Button>
        )}

        {activeSessions.length > 1 && (
          <Button
            variant="outlined"
            startIcon={closingAll ? <CircularProgress size={20} /> : <DeleteSweep />}
            onClick={handleCloseAllSessions}
            disabled={closingAll || closingSessionId !== null}
            fullWidth
            color="error"
          >
            Close All Sessions
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
